import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { AdhaarOCRVerficiationAction, PanOCRVerificationAction } from '../../../../v3/Beneficiary/StaffInsurance/APIs/action';
import { ReusableOCRFlow } from '../../../../v3/Common/OCR';
import { getAge } from '../../../Utils';
import { UserFileDict } from '../../../../utils/settings';

const BeneficiaryOCRForm = ({ 
    beneficiary, 
    onFieldChange,
    relations = [],
    errors = {},
    onSubmit,
    submitLoader = false,
    confirmation_text,
    onSave,
    onCancel,
    selectedDocumentType = 'AADHAAR',
    resetTrigger = 0,
    onDocumentTypeChange,
    owner_type_text,
    onRelationUpdate
}) => {
    const [currentRelations, setCurrentRelations] = useState(relations);
    useEffect(() => {
        setCurrentRelations(relations);
    }, [relations]);
    // Handle save with proper data transformation
    const handleSave = async (formData) => {
        const calculatedAge = formData.dob ? getAge(formData.dob) : null;
        const isOCR = formData.ocr_verified === true;
        const beneficiaryData = {
            ...formData,
            age: calculatedAge,
            id_number: isOCR ? (formData.document_id || formData.id_number || '') : null,
            id_type: isOCR ? (selectedDocumentType === 'PAN' ? UserFileDict.PAN_CARD : UserFileDict.AADHAR_CARD) : null,
            document_type: isOCR ? formData.document_type : null,
            ocr_verified: isOCR,
        };
        // Update the state first and wait for it to complete
        if (onFieldChange) {
            await new Promise(resolve => {
                onFieldChange(beneficiaryData);
                // Small delay to ensure state is updated
                setTimeout(resolve, 10);
            });
        }
        
        // Then submit
        if(onSubmit){
            console.log("Submitting beneficiary data:", beneficiaryData);
            await onSubmit(beneficiaryData);
        }
    };

    // Custom data extractor for Aadhaar OCR response
    const aadhaarExtractor = (data) => ({
        full_name: data.name || '',
        dob: data.date_of_birth || '',
        gender: data.gender || '',
        id_number: data.document_id || '',
        document_type: 'AADHAAR'
    });

    // Custom data extractor for PAN OCR response
    const panExtractor = (data) => ({
        full_name: data.name || '',
        dob: data.date_of_birth || '',
        id_number: data.document_id || '',
        document_type: 'PAN'
    });

    // Select the appropriate OCR service and extractor based on document type
    const ocrService = selectedDocumentType === 'PAN' ? PanOCRVerificationAction : AdhaarOCRVerficiationAction;
    const extractorFunction = selectedDocumentType === 'PAN' ? panExtractor : aadhaarExtractor;
    const uploadTitle = selectedDocumentType === 'PAN' ? 'Upload Beneficiary PAN' : 'Upload Beneficiary Aadhaar';
    const uploadedTitle = selectedDocumentType === 'PAN' ? 'Uploaded Beneficiary PAN' : 'Uploaded Beneficiary Aadhaar';
    const processingText = selectedDocumentType === 'PAN' ? 'Processing PAN...' : 'Processing Aadhar...';

    // Additional fields for PAN document type (gender dropdown)
    const additionalFields = selectedDocumentType === 'PAN' ? [
        {
            type: 'select',
            name: 'gender',
            label: 'Gender',
            placeholder: 'Select Gender',
             required: true,
            defaultValue: 'MALE', // Select first gender by default
            options: [
                { value: 'MALE', label: 'Male' },
                { value: 'FEMALE', label: 'Female' }
            ]
        }
    ] : [];

    // Debug wrapper for onRelationUpdate
    const handleRelationUpdate = (newRelations) => {
        setCurrentRelations(newRelations);
        if (onRelationUpdate) {
            onRelationUpdate(newRelations);
        }
    };

    return (
        <Box>
            <ReusableOCRFlow
                ocrService={ocrService}
                extractorFunction={extractorFunction}
                title="Add Beneficiary Details"
                uploadTitle={uploadTitle}
                uploadedTitle={uploadedTitle}
                processingText={processingText}
                context="beneficiary"
                requirePhone={true}
                requireEmail={false}
                requireRelation={true}
                relations={currentRelations}
                minAge={18}
                maxAge={65}
                onSave={handleSave}
                onCancel={onCancel}
                confirmModalTitle="Confirm Beneficiary Details"
                confirmationCheckboxText={confirmation_text.label}
                onFieldChange={onFieldChange}
                additionalFields={additionalFields}
                resetTrigger={resetTrigger}
                showDocumentTypeSelection={true}
                selectedDocumentType={selectedDocumentType}
                onDocumentTypeChange={onDocumentTypeChange}
                relationData={beneficiary.relation}
                initialData={{
                    phone_number: beneficiary.phone_number || '',
                    relation: beneficiary.relation || ''
                }}
                owner_type_text={owner_type_text}
                onRelationUpdate={handleRelationUpdate}
                showManualEntry={false}
            />
        </Box>
    );
};

export default BeneficiaryOCRForm;